/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { InfoCircleOutlined } from '@ant-design/icons'
import React, { useCallback, useState } from 'react'
import { connect } from 'react-redux'
import colors from '@sportninja/common/constants/appColors'
import { getSoccerSport } from '@sportninja/common/selectors/types'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import BaseModal from '../ts-components/BaseModal'
import Button from '../ts-components/Button'
import Text from '../ts-components/Text'
import ManageTeamAttendanceModalContent from './ManageTeamAttendanceModalContent'
import AttendanceHelpModal from './components/AttendanceHelpModal'
import PlayerFinderModal from 'src/features/playerFinder/PlayerFinderModal'

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
`

// Title + info (i) icon that opens the attendance-status help modal.
const titleRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 32px;
`

const infoIconStyle = css`
  color: ${colors.PRIMARY};
  font-size: 18px;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.8;
  }
`

type ManageTeamAttendanceModalProps = {
  isManageAttendanceModalOpen: boolean
  setIsManageAttendanceModalOpen: (isOpen: boolean) => void
  selectedManageAttendance: {
    gameId: string
    rosterId: string
    teamId: string
    teamName: string
    showWaiver: boolean
  } | null
  readAttendanceInformation: () => void
  soccerSportId?: number
}

const ManageTeamAttendanceModal: React.FC<ManageTeamAttendanceModalProps> = ({
  isManageAttendanceModalOpen,
  setIsManageAttendanceModalOpen,
  selectedManageAttendance,
  readAttendanceInformation,
  soccerSportId,
}) => {
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [isFinderOpen, setIsFinderOpen] = useState(false)

  const handleClose = useCallback(() => {
    readAttendanceInformation()
    setIsManageAttendanceModalOpen(false)
  }, [readAttendanceInformation, setIsManageAttendanceModalOpen])

  if (!isManageAttendanceModalOpen || !selectedManageAttendance) {
    return null
  }

  const title = (
    <div css={titleRowStyle}>
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        {capitalizeWords(`Attendance - ${selectedManageAttendance.teamName}`)}
      </Text>
      <InfoCircleOutlined
        css={infoIconStyle}
        onClick={() => setIsHelpOpen(true)}
        title='Attendance status help'
        aria-label='Attendance status help'
      />
    </div>
  )

  const actions = (
    <div css={footerStyle}>
      <Button
        text='Add Player with Player Finder'
        variant='primary'
        style={'flex: 0 0 auto; width: auto;'}
        onClick={() => setIsFinderOpen(true)}
      />
    </div>
  )

  return (
    <>
      <BaseModal
        isOpen={isManageAttendanceModalOpen}
        onClose={handleClose}
        title={title}
        desktopWidth='748px'
        actions={actions}
        removeBodyPaddingX
      >
        <ManageTeamAttendanceModalContent
          gameId={selectedManageAttendance.gameId}
          rosterId={selectedManageAttendance.rosterId}
          showWaiver={selectedManageAttendance.showWaiver}
          soccerSportId={soccerSportId}
        />
      </BaseModal>
      <AttendanceHelpModal
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
      />
      <PlayerFinderModal
        isOpen={isFinderOpen}
        onClose={() => setIsFinderOpen(false)}
        gameId={selectedManageAttendance.gameId}
        rosterId={selectedManageAttendance.rosterId}
        teamId={selectedManageAttendance.teamId}
      />
    </>
  )
}

const mapStateToProps = (state: any) => ({
  soccerSportId: getSoccerSport(state)?.id,
})

export default connect(mapStateToProps)(ManageTeamAttendanceModal)
